import { useReducer, useCallback, useMemo } from 'react';
import {
    MATCH_MODES,
    MAX_RULES,
    validateAllRules,
    serializeFilterSchema,
    pruneRulesForType,
    buildDefaultPopupFields,
    getValidPopupFieldIds,
    serializePopupSchema,
} from '../utils/discoverLayerFilterSchema';

/**
 * Wizard step identifiers in display order.
 */
export const WIZARD_STEPS = Object.freeze(['basics', 'filters', 'popup']);
export const TOTAL_STEPS = WIZARD_STEPS.length;

/** Valid object types for custom layers. */
export const LAYER_OBJECT_TYPES = Object.freeze([
    'ARTIST', 'BAND', 'EVENT', 'SESSION', 'VENUE',
]);

/** Layer name constraints. */
export const LAYER_NAME_MIN = 3;
export const LAYER_NAME_MAX = 80;

// eslint-disable-next-line no-control-regex
const CONTROL_CHAR_RE = /[\u0000-\u001F\u007F]/;

/**
 * Validate a layer name and return an error key or null.
 * @param {string} raw - Raw input value
 * @param {string[]} existingNames - Names of existing layers (for duplicate check)
 * @returns {string|null} i18n error key suffix under `discoverPage.layerWizard.basics.errors.*`, or null if valid
 */
export function validateLayerName(raw, existingNames = []) {
    const trimmed = (raw ?? '').trim();
    if (trimmed.length === 0) return 'nameRequired';
    if (CONTROL_CHAR_RE.test(trimmed)) return 'nameInvalidChars';
    if (trimmed.length < LAYER_NAME_MIN) return 'nameTooShort';
    if (trimmed.length > LAYER_NAME_MAX) return 'nameTooLong';
    const lower = trimmed.toLowerCase();
    if (existingNames.some((n) => n.toLowerCase() === lower)) return 'nameDuplicate';
    return null;
}

// --- Action types -----------------------------------------------------------

const NEXT_STEP = 'NEXT_STEP';
const PREV_STEP = 'PREV_STEP';
const MARK_DIRTY = 'MARK_DIRTY';
const SET_STEP_VALID = 'SET_STEP_VALID';
const SET_NAME = 'SET_NAME';
const SET_OBJECT_TYPE = 'SET_OBJECT_TYPE';
const SET_MATCH_MODE = 'SET_MATCH_MODE';
const ADD_RULE = 'ADD_RULE';
const UPDATE_RULE = 'UPDATE_RULE';
const REMOVE_RULE = 'REMOVE_RULE';
const DISMISS_PRUNE_NOTICE = 'DISMISS_PRUNE_NOTICE';
const SET_POPUP_FIELDS = 'SET_POPUP_FIELDS';
const TOGGLE_POPUP_FIELD = 'TOGGLE_POPUP_FIELD';
const MOVE_POPUP_FIELD = 'MOVE_POPUP_FIELD';
const SET_POPUP_HIDE_EMPTY = 'SET_POPUP_HIDE_EMPTY';
const LOAD_LAYER = 'LOAD_LAYER';
const RESET = 'RESET';

// --- Initial state -----------------------------------------------------------

function buildInitialState() {
    return {
        currentStep: 0,
        isDirty: false,
        editingLayerId: null,
        /**
         * Per-step validity map keyed by step index.
         * Step 0 (basics) defaults invalid (name required).
         * Steps 1-2 default valid (empty filters/popup are acceptable per plan).
         */
        stepValidity: { 0: false, 1: true, 2: true },
        /** Draft layer name (raw input). */
        name: '',
        /** Draft object type (one of LAYER_OBJECT_TYPES or ''). */
        objectType: '',
        /** Filter match mode: ALL (AND) or ANY (OR). */
        matchMode: MATCH_MODES.ALL,
        /** Array of filter rule drafts. */
        rules: [],
        /** Auto-incrementing id counter for stable rule keys. */
        nextRuleId: 1,
        /** Number of rules pruned by the last type switch (for user notice). */
        prunedRuleCount: 0,
        /** Ordered list of selected popup field ids. */
        popupFields: [],
        /** Whether to hide empty fields in popup. */
        popupHideEmpty: false,
    };
}

// --- Reducer -----------------------------------------------------------------

function wizardReducer(state, action) {
    switch (action.type) {
        case NEXT_STEP: {
            if (!state.stepValidity[state.currentStep]) return state;
            if (state.currentStep >= TOTAL_STEPS - 1) return state;
            return { ...state, currentStep: state.currentStep + 1 };
        }
        case PREV_STEP: {
            if (state.currentStep <= 0) return state;
            return { ...state, currentStep: state.currentStep - 1 };
        }
        case MARK_DIRTY: {
            if (state.isDirty) return state;
            return { ...state, isDirty: true };
        }
        case SET_STEP_VALID: {
            const { step, valid } = action.payload;
            if (step < 0 || step >= TOTAL_STEPS) return state;
            if (state.stepValidity[step] === valid) return state;
            return {
                ...state,
                stepValidity: { ...state.stepValidity, [step]: valid },
            };
        }
        case SET_NAME:
            return { ...state, name: action.payload, isDirty: true };
        case SET_OBJECT_TYPE: {
            const newType = action.payload;
            if (newType === state.objectType) return state;
            // Auto-prune rules incompatible with the new type
            let nextRules = state.rules;
            let nextPrunedCount = 0;
            if (state.rules.length > 0) {
                const { kept, pruned } = pruneRulesForType(state.rules, newType);
                nextRules = kept;
                nextPrunedCount = pruned.length;
            }
            // Re-initialize popup fields for the new type
            const nextPopupFields = buildDefaultPopupFields(newType);
            return {
                ...state,
                objectType: newType,
                rules: nextRules,
                prunedRuleCount: nextPrunedCount,
                popupFields: nextPopupFields,
                isDirty: true,
            };
        }
        case SET_MATCH_MODE:
            return { ...state, matchMode: action.payload, isDirty: true };
        case ADD_RULE: {
            if (state.rules.length >= MAX_RULES) return state;
            const newRule = {
                id: state.nextRuleId,
                fieldId: '',
                operator: '',
                value: '',
                secondValue: '',
            };
            return {
                ...state,
                rules: [...state.rules, newRule],
                nextRuleId: state.nextRuleId + 1,
                isDirty: true,
            };
        }
        case UPDATE_RULE: {
            const { ruleId, changes } = action.payload;
            return {
                ...state,
                rules: state.rules.map((r) => (r.id === ruleId ? { ...r, ...changes } : r)),
                isDirty: true,
            };
        }
        case REMOVE_RULE:
            return {
                ...state,
                rules: state.rules.filter((r) => r.id !== action.payload),
                isDirty: true,
            };
        case DISMISS_PRUNE_NOTICE:
            if (state.prunedRuleCount === 0) return state;
            return { ...state, prunedRuleCount: 0 };
        case SET_POPUP_FIELDS:
            return { ...state, popupFields: action.payload, isDirty: true };
        case TOGGLE_POPUP_FIELD: {
            const fieldId = action.payload;
            const idx = state.popupFields.indexOf(fieldId);
            if (idx >= 0) {
                // Remove — but keep at least 1
                if (state.popupFields.length <= 1) return state;
                return {
                    ...state,
                    popupFields: state.popupFields.filter((id) => id !== fieldId),
                    isDirty: true,
                };
            }
            return {
                ...state,
                popupFields: [...state.popupFields, fieldId],
                isDirty: true,
            };
        }
        case MOVE_POPUP_FIELD: {
            const { fieldId: moveId, direction } = action.payload;
            const currentIdx = state.popupFields.indexOf(moveId);
            if (currentIdx < 0) return state;
            const targetIdx = direction === 'up' ? currentIdx - 1 : currentIdx + 1;
            if (targetIdx < 0 || targetIdx >= state.popupFields.length) return state;
            const reordered = [...state.popupFields];
            [reordered[currentIdx], reordered[targetIdx]] = [reordered[targetIdx], reordered[currentIdx]];
            return { ...state, popupFields: reordered, isDirty: true };
        }
        case SET_POPUP_HIDE_EMPTY:
            return { ...state, popupHideEmpty: Boolean(action.payload), isDirty: true };
        case LOAD_LAYER: {
            const layer = action.payload;
            const objectType = layer.objectType || '';
            const filterRules = (layer.filters?.rules || []).map((r, idx) => ({
                id: idx + 1,
                fieldId: r.fieldId || '',
                operator: r.operator || '',
                value: Array.isArray(r.value) ? r.value.join(', ') : (r.value ?? ''),
                secondValue: r.secondValue ?? '',
            }));
            let popupFields;
            if (Array.isArray(layer.popup?.fields) && layer.popup.fields.length > 0) {
                // Prune stale/removed field IDs that are no longer valid for this type
                const validIds = getValidPopupFieldIds(objectType);
                const pruned = layer.popup.fields.filter((id) => validIds.has(id));
                popupFields = pruned.length > 0 ? pruned : buildDefaultPopupFields(objectType);
            } else {
                popupFields = buildDefaultPopupFields(objectType);
            }
            return {
                ...buildInitialState(),
                editingLayerId: layer.id || null,
                name: layer.name || '',
                objectType,
                matchMode: layer.filters?.match || MATCH_MODES.ALL,
                rules: filterRules,
                nextRuleId: filterRules.length + 1,
                popupFields,
                popupHideEmpty: Boolean(layer.popup?.hideEmpty),
                stepValidity: { 0: true, 1: true, 2: true },
            };
        }
        case RESET:
            return buildInitialState();
        default:
            return state;
    }
}

// --- Hook --------------------------------------------------------------------

/**
 * Deterministic wizard state machine for the Discover layer wizard.
 *
 * Provides step navigation gated by per-step validity, dirty tracking,
 * draft data (name, objectType), and reset capability.
 */
export default function useDiscoverLayerWizard() {
    const [state, dispatch] = useReducer(wizardReducer, undefined, buildInitialState);

    const goNext = useCallback(() => dispatch({ type: NEXT_STEP }), []);
    const goBack = useCallback(() => dispatch({ type: PREV_STEP }), []);
    const markDirty = useCallback(() => dispatch({ type: MARK_DIRTY }), []);
    const reset = useCallback(() => dispatch({ type: RESET }), []);

    const setStepValid = useCallback((step, valid) => {
        dispatch({ type: SET_STEP_VALID, payload: { step, valid: Boolean(valid) } });
    }, []);

    const setName = useCallback((value) => {
        dispatch({ type: SET_NAME, payload: value });
    }, []);

    const setObjectType = useCallback((value) => {
        dispatch({ type: SET_OBJECT_TYPE, payload: value });
    }, []);

    const setMatchMode = useCallback((value) => {
        dispatch({ type: SET_MATCH_MODE, payload: value });
    }, []);

    const addRule = useCallback(() => {
        dispatch({ type: ADD_RULE });
    }, []);

    const updateRule = useCallback((ruleId, changes) => {
        dispatch({ type: UPDATE_RULE, payload: { ruleId, changes } });
    }, []);

    const removeRule = useCallback((ruleId) => {
        dispatch({ type: REMOVE_RULE, payload: ruleId });
    }, []);

    const dismissPruneNotice = useCallback(() => {
        dispatch({ type: DISMISS_PRUNE_NOTICE });
    }, []);

    const setPopupFields = useCallback((fields) => {
        dispatch({ type: SET_POPUP_FIELDS, payload: fields });
    }, []);

    const togglePopupField = useCallback((fieldId) => {
        dispatch({ type: TOGGLE_POPUP_FIELD, payload: fieldId });
    }, []);

    const movePopupField = useCallback((fieldId, direction) => {
        dispatch({ type: MOVE_POPUP_FIELD, payload: { fieldId, direction } });
    }, []);

    const setPopupHideEmpty = useCallback((value) => {
        dispatch({ type: SET_POPUP_HIDE_EMPTY, payload: value });
    }, []);

    const loadLayer = useCallback((layer) => {
        dispatch({ type: LOAD_LAYER, payload: layer });
    }, []);

    /** Per-rule validation errors for UI display. */
    const ruleErrors = useMemo(
        () => validateAllRules(state.rules, state.objectType).errors,
        [state.rules, state.objectType],
    );

    const isFirstStep = state.currentStep === 0;
    const isLastStep = state.currentStep === TOTAL_STEPS - 1;
    const canGoNext = state.stepValidity[state.currentStep] && !isLastStep;
    const currentStepKey = WIZARD_STEPS[state.currentStep];

    const isEditing = Boolean(state.editingLayerId);

    /** Trimmed draft payload for submission. */
    const draftPayload = useMemo(() => {
        const payload = {
            name: (state.name ?? '').trim(),
            objectType: state.objectType,
            filters: serializeFilterSchema(state.matchMode, state.rules),
            popup: serializePopupSchema(state.popupFields, state.popupHideEmpty),
        };
        if (state.editingLayerId) {
            payload.id = state.editingLayerId;
        }
        return payload;
    }, [state.editingLayerId, state.name, state.objectType, state.matchMode, state.rules, state.popupFields, state.popupHideEmpty]);

    return {
        currentStep: state.currentStep,
        currentStepKey,
        isDirty: state.isDirty,
        isEditing,
        stepValidity: state.stepValidity,
        isFirstStep,
        isLastStep,
        canGoNext,
        goNext,
        goBack,
        markDirty,
        setStepValid,
        reset,
        loadLayer,
        name: state.name,
        objectType: state.objectType,
        setName,
        setObjectType,
        matchMode: state.matchMode,
        rules: state.rules,
        ruleErrors,
        setMatchMode,
        addRule,
        updateRule,
        removeRule,
        prunedRuleCount: state.prunedRuleCount,
        dismissPruneNotice,
        popupFields: state.popupFields,
        popupHideEmpty: state.popupHideEmpty,
        setPopupFields,
        togglePopupField,
        movePopupField,
        setPopupHideEmpty,
        draftPayload,
    };
}
